
import React from 'react';
import { Construction } from 'lucide-react';

const PostOpLegacy: React.FC = () => {
  const TextInput = ({ label, defaultValue = "", className = "", placeholder = "" }: { label: string, defaultValue?: string, className?: string, placeholder?: string }) => (
    <div className={`flex items-center space-x-2 mb-1.5 ${className}`}>
      <label className="text-[11px] font-semibold text-gray-700 w-32 shrink-0">{label}:</label>
      <input 
        type="text" 
        defaultValue={defaultValue}
        placeholder={placeholder}
        className="flex-1 border border-blue-200 bg-white px-2 py-0.5 text-[12px] h-7 focus:ring-1 focus:ring-blue-400 focus:outline-none rounded-sm transition-all"
      />
    </div>
  );

  const TimeInput = ({ label, defaultValue = "" }: { label: string, defaultValue?: string }) => (
    <div className="grid grid-cols-4 gap-1 mb-1 items-center">
      <label className="text-[11px] font-semibold text-gray-600 text-right pr-2">{label}:</label>
      <input type="text" defaultValue={defaultValue} placeholder="Time" className="border border-blue-100 p-0.5 text-[11px] text-center rounded-sm" />
      <input type="text" placeholder="AM/PM" className="border border-blue-100 p-0.5 text-[11px] text-center rounded-sm" />
      <input type="text" placeholder="Dur" className="border border-blue-100 p-0.5 text-[11px] text-center rounded-sm bg-gray-50" />
    </div>
  );

  return (
    <div className="h-full bg-slate-100 font-sans p-4 overflow-auto relative">
      {/* Coming Soon Overlay */}
      <div className="absolute inset-0 bg-white/60 backdrop-blur-sm z-50 flex items-center justify-center">
        <div className="bg-white px-8 py-6 rounded-2xl shadow-xl border border-slate-200 flex flex-col items-center">
          <div className="w-16 h-16 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mb-4">
            <Construction className="w-8 h-8" />
          </div>
          <h2 className="text-2xl font-bold text-slate-800 mb-2">Coming Soon</h2>
          <p className="text-slate-500 text-center max-w-xs text-sm">
            We're currently fine-tuning this workflow. Check back later!
          </p>
        </div>
      </div>

      {/* Outer Application Frame */}
      <div className="max-w-7xl mx-auto border-4 border-blue-400 bg-white shadow-2xl rounded-lg overflow-hidden flex flex-col min-h-[850px]">
        {/* Title Bar */}
        <div className="bg-[#5c98d6] p-2 flex justify-between items-center text-white border-b border-blue-600">
          <div className="flex items-center space-x-3">
            <i className="fa-solid fa-laptop-medical"></i>
            <span className="text-sm font-bold tracking-wide">Operating Room Records & Surgical Supply (Legacy)</span>
          </div>
          <div className="flex space-x-4 text-xs font-semibold">
            <span>AARON, JANE W (9851)</span>
            <span>4/09/1980</span>
            <span>Age: 38y</span>
            <div className="flex space-x-1">
              <i className="fa-solid fa-window-minimize px-1 cursor-pointer"></i>
              <i className="fa-solid fa-window-maximize px-1 cursor-pointer"></i>
              <i className="fa-solid fa-xmark px-1 cursor-pointer bg-red-500 rounded-sm"></i>
            </div>
          </div>
        </div>

        {/* Toolbar */}
        <div className="bg-[#f0f7ff] border-b border-blue-200 p-2 flex space-x-4 items-center overflow-x-auto">
          {['Action', 'View', 'Setup', 'Activities', 'Billing', 'Reports', 'Utilities', 'Windows', 'Help'].map(item => (
            <span key={item} className="text-[12px] text-gray-700 cursor-pointer hover:text-blue-600 hover:underline">{item}</span>
          ))}
          <div className="flex-1"></div>
          <div className="flex space-x-2">
            <i className="fa-solid fa-print text-blue-500 cursor-pointer p-1 rounded hover:bg-white shadow-sm"></i>
            <i className="fa-solid fa-envelope text-blue-500 cursor-pointer p-1 rounded hover:bg-white shadow-sm"></i>
            <i className="fa-solid fa-circle-question text-blue-500 cursor-pointer p-1 rounded hover:bg-white shadow-sm"></i>
          </div>
        </div>

        {/* Main Interface Split */}
        <div className="flex-1 flex overflow-hidden">
          {/* Left Navigation Tree */}
          <div className="w-56 bg-[#e1edf9] border-r border-blue-200 flex flex-col">
            <div className="p-3 bg-[#c2d9f1] font-bold text-xs text-blue-900 flex justify-between items-center">
              <span>VISIT NOTE</span>
              <i className="fa-solid fa-chevron-down"></i>
            </div>
            <div className="flex-1 overflow-y-auto">
              {[
                { name: 'General', color: 'bg-yellow-50' },
                { name: 'Allergy', color: 'bg-red-50' },
                { name: 'Current Medication', color: 'bg-blue-100' },
                { name: 'Vital Signs', color: 'bg-orange-50' },
                { name: 'PAT Check List', color: 'bg-green-50' },
                { name: 'Pre-Anesthesia Eval', color: 'bg-blue-50' },
                { name: 'Procedure Note', color: 'bg-gray-50' },
                { name: 'Pre-Op Nurse Note', color: 'bg-purple-50' },
                { name: 'OP Report', color: 'bg-[#5c98d6] text-white', active: true },
                { name: 'Intra-Op Note', color: 'bg-gray-50' },
                { name: 'Intra-Op Pain Note', color: 'bg-gray-50' },
                { name: 'Anesthesia', color: 'bg-green-100' },
                { name: 'Diagnosis/Decision', color: 'bg-green-200 font-bold' },
                { name: 'Post Anesthesia Eval', color: 'bg-purple-100' },
                { name: 'PACU Note', color: 'bg-pink-100' },
                { name: 'Discharge Instruction', color: 'bg-purple-200' },
              ].map(item => (
                <div key={item.name} className={`px-3 py-2 text-[11px] border-b border-blue-100 cursor-pointer hover:brightness-95 transition-all flex items-center justify-between ${item.color} ${item.active ? 'ring-inset ring-2 ring-blue-400' : ''}`}>
                  <span>{item.name}</span>
                  {item.active && <i className="fa-solid fa-pencil text-[10px]"></i>}
                </div>
              ))}
            </div>
          </div>

          {/* Center Main Form */}
          <div className="flex-1 bg-white p-4 overflow-y-auto">
            <div className="bg-[#fffdca] border border-orange-200 p-2 mb-4 rounded shadow-sm text-xs font-bold text-blue-900">
              Preference Card(s): <span className="text-blue-600 underline cursor-pointer ml-1">None Selected</span>
              <div className="float-right text-blue-600 font-bold">O.R. & Surgery Detail</div>
            </div>

            <div className="grid grid-cols-12 gap-6">
              {/* Timing Column */}
              <div className="col-span-4 bg-blue-50/50 p-3 rounded border border-blue-100">
                <h3 className="text-xs font-bold text-blue-800 mb-3 uppercase flex items-center">
                  <i className="fa-regular fa-clock mr-2"></i> Timings
                </h3>
                <TimeInput label="OR" defaultValue="" />
                <TimeInput label="Surgery" defaultValue="" />
                <TimeInput label="RR" defaultValue="" />
                <TimeInput label="Anesthesia" defaultValue="" />
              </div>

              {/* Room Column */}
              <div className="col-span-4 bg-blue-50/50 p-3 rounded border border-blue-100">
                <h3 className="text-xs font-bold text-blue-800 mb-3 uppercase flex items-center">
                  <i className="fa-solid fa-vial mr-2"></i> O.R. Info
                </h3>
                <TextInput label="Minor Room" defaultValue="" placeholder="Room #" />
                <TextInput label="Implant" defaultValue="" placeholder="Serial/Ref" />
                <TextInput label="Frozen Section" defaultValue="" placeholder="Status" />
                <TextInput label="Fluro" defaultValue="" placeholder="Equipment" />
                <div className="flex flex-col mt-2">
                  <label className="text-[11px] font-semibold text-gray-700 mb-1">Procedure Note:</label>
                  <textarea 
                    defaultValue="" 
                    placeholder="Dictate procedure details..."
                    className="border border-blue-200 p-2 text-[12px] h-12 focus:outline-none rounded-sm bg-white"
                  ></textarea>
                </div>
              </div>

              {/* Diagnosis Column */}
              <div className="col-span-4 bg-blue-50/50 p-3 rounded border border-blue-100">
                <h3 className="text-xs font-bold text-blue-800 mb-3 uppercase flex items-center">
                  <i className="fa-solid fa-clipboard-check mr-2"></i> Diagnosis
                </h3>
                <TextInput label="Dx1" defaultValue="" placeholder="ICD-10" />
                <TextInput label="Dx2" defaultValue="" placeholder="ICD-10" />
                <TextInput label="Dx3" defaultValue="" />
                <TextInput label="Dx4" defaultValue="" />
              </div>

              {/* Charges Section */}
              <div className="col-span-12 grid grid-cols-3 gap-6 mt-2">
                <div className="border border-blue-200 rounded p-3 bg-white shadow-sm">
                  <div className="text-[11px] font-bold text-blue-800 border-b border-blue-100 pb-1 mb-2">O.R. CHARGES</div>
                  <TextInput label="Room" defaultValue="" />
                  <TextInput label="Minutes" defaultValue="" />
                  <TextInput label="CPT" defaultValue="" />
                  <TextInput label="Amount" defaultValue="" />
                  <textarea placeholder="O.R. Notes..." className="w-full border border-blue-100 p-1 mt-1 text-[11px] h-12 bg-slate-50"></textarea>
                </div>
                <div className="border border-blue-200 rounded p-3 bg-white shadow-sm">
                  <div className="text-[11px] font-bold text-blue-800 border-b border-blue-100 pb-1 mb-2">R.R. CHARGES</div>
                  <TextInput label="Room" defaultValue="" />
                  <TextInput label="Minutes" defaultValue="" />
                  <TextInput label="CPT" defaultValue="" />
                  <TextInput label="Amount" defaultValue="" />
                  <textarea placeholder="R.R. Notes..." className="w-full border border-blue-100 p-1 mt-1 text-[11px] h-12 bg-slate-50"></textarea>
                </div>
                <div className="border border-blue-200 rounded p-3 bg-white shadow-sm">
                  <div className="text-[11px] font-bold text-blue-800 border-b border-blue-100 pb-1 mb-2">ANESTHESIA FEES</div>
                  <TextInput label="Provider" defaultValue="" />
                  <TextInput label="Minutes" defaultValue="" />
                  <TextInput label="Charge" defaultValue="" />
                  <textarea placeholder="Anesthesia Notes..." className="w-full border border-blue-100 p-1 mt-1 text-[11px] h-12 bg-slate-50"></textarea>
                </div>
              </div>

              {/* Supply Table */}
              <div className="col-span-12 border border-blue-200 rounded mt-4 bg-white overflow-hidden shadow-sm">
                <div className="bg-[#cbdcf1] p-1.5 flex justify-between items-center text-[11px] font-bold">
                  <span>Particulars</span>
                  <div className="flex space-x-4">
                    <span>Serial No.</span>
                    <span className="w-24">Location</span>
                    <span className="w-16">Used Qty</span>
                  </div>
                </div>
                <div className="divide-y divide-blue-50">
                  {[1, 2, 3, 4, 5, 6].map((idx) => (
                    <div key={idx} className="flex p-1.5 text-[11px] hover:bg-blue-50/50">
                      <div className="flex-1 flex items-center space-x-2">
                        <span className="text-blue-500 font-bold w-4">{idx}.</span>
                        <input type="text" defaultValue="" placeholder="Search supply..." className="flex-1 border-none bg-transparent focus:outline-none" />
                      </div>
                      <input type="text" defaultValue="" placeholder="Lot #" className="w-24 border border-blue-50 px-1 mx-2" />
                      <input type="text" placeholder="Loc" className="w-24 border border-blue-50 px-1 mx-2" />
                      <input type="text" defaultValue="" placeholder="Qty" className="w-12 border border-blue-50 px-1 text-center" />
                      <i className="fa-solid fa-note-sticky text-blue-300 ml-2 cursor-pointer hover:text-blue-500"></i>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Footer Buttons */}
            <div className="flex justify-between items-center mt-6 py-4 border-t border-blue-100 bg-[#f9fbff] px-4 -mx-4">
              <div className="flex space-x-3">
                <button className="bg-white border border-blue-300 text-blue-700 px-4 py-1.5 rounded text-xs font-bold hover:bg-blue-50 shadow-sm">Generate SuperBill</button>
                <button className="bg-white border border-blue-300 text-blue-700 px-4 py-1.5 rounded text-xs font-bold hover:bg-blue-50 shadow-sm">Report</button>
                <button className="bg-white border border-blue-300 text-blue-700 px-4 py-1.5 rounded text-xs font-bold hover:bg-blue-50 shadow-sm">Print</button>
              </div>
              <div className="flex space-x-3">
                <button className="bg-[#f2f2f2] border border-gray-400 text-gray-700 px-6 py-1.5 rounded text-xs font-bold hover:bg-gray-200">Cancel</button>
                <button className="bg-[#3e78b1] border border-blue-700 text-white px-8 py-1.5 rounded text-xs font-bold hover:bg-blue-700 shadow-md">
                  <i className="fa-solid fa-floppy-disk mr-2"></i> Save Record
                </button>
              </div>
            </div>
          </div>

          {/* Right Action Sidebar (Compact) */}
          <div className="w-12 bg-[#2d527a] flex flex-col items-center py-4 space-y-6 text-white/70">
            <i className="fa-solid fa-file-export cursor-pointer hover:text-white" title="Export"></i>
            <i className="fa-solid fa-chart-line cursor-pointer hover:text-white" title="Dashboard"></i>
            <i className="fa-solid fa-pills cursor-pointer hover:text-white" title="Meds"></i>
            <i className="fa-solid fa-calendar-check cursor-pointer hover:text-white" title="Calendar"></i>
            <i className="fa-solid fa-message cursor-pointer hover:text-white" title="Messages"></i>
            <i className="fa-solid fa-gear cursor-pointer hover:text-white" title="Settings"></i>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PostOpLegacy;